import ObjectModel from "@src/backbone/prototypes/ObjectModel";
import moment from "moment";
import { EHappyHourLocation } from "@src/lib/happyHour/types/HappyHour.interface";
import { HappyHourModelAttributes } from "../interface/HappyHourAttribute";

const DEFAULT_HAPPY_HOUR_RATE = 1;
const CACHE_DURATION = 30 * 1000; // 30 seconds cache duration

class HappyHourModel extends ObjectModel<HappyHourModelAttributes> {
  name = "HappyHourModel";
  idAttribute = "id_cart";
  private _fetchCache: {
    promise?: Promise<HappyHourModel>;
    timestamp?: number;
  } = {};

  defaults = () => {
    return {
      id_cart: "current",
      rate: DEFAULT_HAPPY_HOUR_RATE,
    };
  };

  private shouldRefreshCache(): boolean {
    // timestamp absent tant que la requête est en cours
    // ne pas la traiter comme expirée si une promise est déjà en cours.
    if (!this._fetchCache.timestamp) return !this._fetchCache.promise;
    return Date.now() - this._fetchCache.timestamp > CACHE_DURATION;
  }

  private clearCache(): void {
    this._fetchCache = {};
  }

  fetchWithCache(
    options?: Backbone.ModelFetchOptions
  ): Promise<HappyHourModel> {
    // Return cached promise if it exists and is still valid
    if (this._fetchCache.promise && !this.shouldRefreshCache()) {
      return this._fetchCache.promise;
    }

    // Clear existing cache
    this.clearCache();

    // Create new fetch promise
    this._fetchCache.promise = new Promise((resolve, reject) => {
      this.fetch({
        ...options,
        success: (model, response) => {
          this._fetchCache.timestamp = Date.now();
          options?.success?.(model, response, options);
          resolve(this);
        },
        error: (model, error, opts) => {
          this.clearCache();
          options?.error?.(model, error, opts);
          reject(error);
        },
      });
    });

    return this._fetchCache.promise;
  }

  fetch(options?: Backbone.ModelFetchOptions): JQueryXHR {
    const self = this;
    self.once("sync", () => {
      const isNowHappyHour = self.isNowHappyHour(),
        startDate = self.getStartDate(),
        endDate = self.getEndDate();

      const refresh = (delay: number) => {
        setTimeout(() => {
          self.clearCache(); // Clear cache before auto-refresh
          self.fetch(options);
        }, (delay + 1) * 1000);
      };

      if (isNowHappyHour) {
        const secondsToEnd = endDate?.diff(new Date(), "s") || 0;
        if (secondsToEnd > 0 && secondsToEnd <= 60 * 60 * 6) {
          refresh(secondsToEnd);
        }
      } else {
        const secondsToStart = startDate?.diff(new Date(), "s") || 0;
        if (secondsToStart > 0 && secondsToStart <= 60 * 60 * 6) {
          refresh(secondsToStart);
        }
      }

      self.trigger("happyHourSynced", self);
    });
    return super.fetch(options);
  }
  getDate(date_key: "start_date" | "end_date"): moment.Moment | null {
    const self = this,
      date = self.get(date_key);
    if (!date) return null;
    return moment(date);
  }
  getStartDate = (): moment.Moment | null => {
    return this.getDate("start_date");
  };

  getEndDate = (): moment.Moment | null => {
    return this.getDate("end_date");
  };

  isLast20Minutes = (): boolean => {
    const self = this,
      rate = self.getRate(),
      endDate = self.getEndDate();
    if (!endDate || !self.isNowHappyHour() || rate === DEFAULT_HAPPY_HOUR_RATE)
      return false;
    const now = moment();
    return endDate.diff(now, "minutes") <= 20 && endDate.isAfter(now);
  };

  getMessage = (location: EHappyHourLocation): string => {
    const messages = this.get("messages");
    if (!messages) return "";
    const m = messages[location];
    return m?.message || "";
  };

  getRate = (): number => {
    const rate = this.get("rate");
    return rate || DEFAULT_HAPPY_HOUR_RATE;
  };
  isNowHappyHour = (): boolean => {
    return !!this.get("is_now_happy_hour");
  };

  url = (): string => {
    return `/happyhour`;
  };
}

export default HappyHourModel;
